/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';

import { Icon } from 'sarsaparilla';

const propTypes = {
    result: PropTypes.string,
    onSelect: PropTypes.func,
    onCompletedResult: PropTypes.func,
};

const defaultProps = {};

export default class QuestionnaireResult extends React.Component {
    onChange(choice, e) {
        e.preventDefault();
        this.props.onSelect(choice);
    }

    convertResultToDisplay() {
        if (this.props.result === 'overnight-hike') {
            return 'Overnight Hiking Permit';
        }
        if (this.props.result === 'overnight-paddle') {
            return 'Overnight Paddle Permit';
        }
        if (this.props.result === 'overnight-paddle-entry-71') {
            return 'Overnight Paddle Permit for entry #71';
        }
        if (this.props.result === 'overnight-motor-entry-71') {
            return 'Overnight Motor Permit for entry #71';
        }
        if (this.props.result === 'overnight-paddle-canada') {
            return 'Overnight Paddle Permit';
        }
        if (this.props.result === 'overnight-motor') {
            return 'Overnight Motor Permit';
        }
        if (this.props.result === 'day-use-motor') {
            return 'Day Use Motor Permit';
        }
        if (this.props.result === 'day-use-motor-to-canada') {
            return 'Day Use Motor Permit';
        }
        if (this.props.result === 'overnight-motor-canada') {
            return 'Overnight Motor Permit';
        }
        if (this.props.result === 'call-fs') {
            return 'Call the Forest Service Office';
        }
        if (this.props.result === 'self-issued') {
            return 'Self Issued Permit';
        }
        return this.props.result;
    }

    isResultReservable() {
        return this.props.result !== 'call-fs' && this.props.result !== 'self-issued';
    }

    renderResult() {
        const isReservable = this.isResultReservable();

        if (isReservable) {
            let description = <p>{this.props.result}</p>;
            if (this.props.result === 'overnight-hike') {
                description = (
                    <p>
                        Thank you for your time! Based on the information provided, you
                        will need an Overnight Hiking Permit for location you are
                        entering. No stay limit for number of nights.
                    </p>
                );
            }
            if (this.props.result === 'overnight-paddle') {
                description = (
                    <p>
                        Thank you for your time! Based on the information provided, you
                        will need an Overnight Paddle Permit for the location you are
                        entering.
                    </p>
                );
            }
            if (this.props.result === 'overnight-paddle-entry-71') {
                description = (
                    <p>
                        Thank you for your time! Based on the information provided, you
                        will need an Overnight Paddle Permit for entry #71 for the date
                        you are entering.
                    </p>
                );
            }
            if (this.props.result === 'overnight-motor-entry-71') {
                description = (
                    <p>
                        Thank you for your time! Based on the information provided, you
                        will need an Overnight Motor Permit for entry #71. Your Entry Date
                        is the day you plan to enter the U.S. You may enter at any
                        location in the BWCAW.
                    </p>
                );
            }
            if (this.props.result === 'overnight-paddle-canada') {
                description = (
                    <p>
                        Thank you for your time! Based on the information provided, you
                        will need an Overnight Paddle Permit for the date you are
                        entering.
                    </p>
                );
            }
            if (this.props.result === 'overnight-motor') {
                description = (
                    <p>
                        Thank you for your time! Based on the information provided, you
                        will need an Overnight Motor Permit for the location you are
                        entering. No stay limit for number of nights.
                    </p>
                );
            }
            if (this.props.result === 'day-use-motor') {
                description = (
                    <p>
                        Thank you for your time! Based on the information provided, you
                        will need a Day Use Motor Permit for the location and day you are
                        entering and must exit the same day. For this permit, you choose
                        the week that you wish to enter. When your permit is issued, it is
                        only valid for the specific entry date identified on your permit.
                    </p>
                );
            }
            if (this.props.result === 'day-use-motor-to-canada') {
                description = (
                    <p>
                        Thank you for your time! Based on the information provided, you
                        will need a Day Use Motor to Canada Permit, each time you enter,
                        for the location and day you are entering and must exit the same
                        day. For this permit, you reserve a week for each leg of the trip
                        that you wish to enter BW and select a specific day when you get
                        your permit issues. When your permit is issued, it is only valid
                        for the specific entry date identified on your permit.
                    </p>
                );
            }
            if (this.props.result === 'overnight-motor-canada') {
                description = (
                    <p>
                        Thank you for your time! Based on the information provided, you
                        need an Overnight Motor Permit for the location and date you begin
                        your trip in the BWCAW. Regardless of whether the night in the
                        BWCAW is at the beginning or the end of the trip (or both), only
                        one Overnight Motor Permit is required.
                    </p>
                );
            }
            return (
                <div>
                    <h1 className="h3 rec-questionnaire-result-what-you-need">
                        You need a...
                    </h1>
                    <div className="rec-questionnaire-result-title">
                        {this.convertResultToDisplay()}
                    </div>
                    <div className="rec-questionnaire-result-icon">
                        <Icon iconName="permit" />
                    </div>
                    {description}
                    <button
                        className="rec-button-primary"
                        title="check availability"
                        onClick={this.props.onCompletedResult}
                        aria-label="check availability"
                    >
                        Check Availability
                    </button>
                </div>
            );
        }

        if (this.props.result === 'self-issued') {
            return (
                <div>
                    <h3 className="rec-questionnaire-result-what-you-need">
                        You need a...
                    </h3>
                    <div className="rec-questionnaire-result-title">
                        {this.convertResultToDisplay()}
                    </div>
                    <div className="rec-questionnaire-result-icon">
                        <Icon iconName="permit" />
                    </div>
                    <p>
                        Thank you for your time! Based on the information provided, you
                        are required to have a Self Issued Permit available from kiosks at
                        BWCAW entry points and Forest Service offices (no reservation
                        required and no recreation fees).
                    </p>
                    <p>
                        Note: If traveling to or from Canada but not staying overnight in
                        BWCAW, you will need a Self Issued Permit for each day you enter
                        the BWCAW.
                    </p>
                </div>
            );
        }

        if (this.props.result === 'call-fs') {
            return (
                <div>
                    <h3 className="rec-questionnaire-result-what-you-need">
                        You need to...
                    </h3>
                    <div className="rec-questionnaire-result-title">
                        {this.convertResultToDisplay()}
                    </div>
                    <div className="rec-questionnaire-result-icon">
                        <Icon iconName="phone" />
                    </div>
                    <p>
                        Thank you for your time! Based on the information provided, you
                        need to call the Forest Service Office for help on selecting the
                        best permit.
                    </p>
                </div>
            );
        }

        return null;
    }

    render() {
        return (
            <div className="rec-questionnaire-result">
                <div className="rec-questionnaire-finished">Finished</div>
                {this.renderResult()}
            </div>
        );
    }
}

QuestionnaireResult.displayName = 'QuestionnaireResult';
QuestionnaireResult.propTypes = propTypes;
QuestionnaireResult.defaultProps = defaultProps;
